import React, { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Shield } from "@/components/Logo";
import api from "@/lib/api";
import {
  LayoutDashboard, Users, PhoneCall, Sparkles, Calculator, BookOpen, FileSignature,
  HardHat, Landmark, TrendingUp, Bot, CloudLightning, Palette, LogOut, Search, Menu, X, Camera, UserCog,
  CalendarDays, Receipt, Star, BarChart3, Zap, Paintbrush, Layers,
} from "lucide-react";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/crm", label: "CRM Pipeline", icon: Users },
  { to: "/receptionist", label: "AI Receptionist", icon: PhoneCall },
  { to: "/design", label: "AI Design & Price", icon: Sparkles },
  { to: "/estimating", label: "Estimating", icon: Calculator },
  { to: "/scheduling", label: "Scheduling", icon: CalendarDays },
  { to: "/pricebook", label: "PriceBook", icon: BookOpen },
  { to: "/scope-templates", label: "Scope Templates", icon: Layers },
  { to: "/proposals", label: "Proposals", icon: FileSignature },
  { to: "/production", label: "Production", icon: HardHat },
  { to: "/invoices", label: "Invoices", icon: Receipt },
  { to: "/quickbooks", label: "QuickBooks", icon: Landmark },
  { to: "/cashflow", label: "Cash Flow", icon: TrendingUp },
  { to: "/analytics", label: "Analytics", icon: BarChart3 },
  { to: "/reviews", label: "Reviews", icon: Star },
  { to: "/ai", label: "AI Business Mgr", icon: Bot },
  { to: "/hailtrace", label: "Hail Trace", icon: CloudLightning },
  { to: "/quote-widget", label: "Quote Widget", icon: Zap },
  { to: "/team", label: "Team", icon: UserCog, ownerOnly: true },
  { to: "/branding", label: "Branding", icon: Paintbrush, ownerOnly: true },
  { to: "/style-guide", label: "Style Guide", icon: Palette },
];

const MOBILE_NAV = [
  { to: "/", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/crm", label: "CRM", icon: Users },
  { to: "/production", label: "Jobs", icon: HardHat },
  { to: "/receptionist", label: "Calls", icon: PhoneCall },
  { to: "/ai", label: "Ask AI", icon: Bot },
];

function SearchBar() {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const nav = useNavigate();
  const run = async (v) => {
    setQ(v);
    if (v.length < 2) return setRes(null);
    const { data } = await api.get(`/search?q=${encodeURIComponent(v)}`);
    setRes(data);
  };
  return (
    <div className="relative w-full max-w-md">
      <div className="flex items-center gap-2 bg-white border-2 border-slate-200 rounded-xl px-3 py-2 focus-within:border-navy transition-colors">
        <Search className="w-4 h-4 text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => run(e.target.value)}
          placeholder="Search contacts, properties, jobs…"
          className="bg-transparent outline-none text-sm w-full"
        />
      </div>
      {res && (q.length >= 2) && (
        <div className="absolute z-40 mt-2 w-full bg-white rounded-xl border border-slate-200 shadow-xl p-2 max-h-80 overflow-auto mb-scroll" data-testid="search-results">
          {[...(res.contacts || []).map((c) => ({ ...c, _t: "contact" })),
            ...(res.jobs || []).map((j) => ({ ...j, _t: "job" }))].slice(0, 8).map((r, i) => (
            <button key={i} data-testid="search-result-item"
              onClick={() => { setQ(""); setRes(null); nav(r._t === "contact" ? `/crm/contact/${r.id}` : `/production/job/${r.id}`); }}
              className="w-full text-left px-3 py-2 rounded-lg hover:bg-slate-50 text-sm flex justify-between">
              <span className="font-medium text-navy">{r.name || r.customer_name}</span>
              <span className="text-xs text-slate-400 uppercase font-semibold">{r._t}</span>
            </button>
          ))}
          {(!res.contacts?.length && !res.jobs?.length) && <div className="px-3 py-2 text-sm text-slate-400">No matches</div>}
        </div>
      )}
    </div>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();

  return (
    <div className="min-h-screen bg-cloud flex">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex flex-col w-64 bg-navy text-white fixed inset-y-0 z-30">
        <div className="px-5 py-5 border-b border-white/10">
          <Shield size={40} withText />
        </div>
        <nav className="flex-1 overflow-y-auto mb-scroll px-3 py-4 space-y-1">
          {NAV.filter((n) => !n.ownerOnly || user?.role === "owner").map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-150 ${
                  isActive ? "bg-brandred text-white shadow" : "text-slate-300 hover:bg-white/10 hover:text-white"}`}>
              <n.icon className="w-4.5 h-4.5" strokeWidth={2.2} style={{ width: 18, height: 18 }} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full bg-brandred flex items-center justify-center font-display font-bold text-sm">
              {(user?.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("")}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate">{user?.name}</div>
              <div className="text-[11px] text-slate-400 uppercase tracking-wide">{user?.role}</div>
            </div>
            <button data-testid="logout-btn" onClick={() => { logout(); nav("/login"); }} className="text-slate-400 hover:text-white">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="relative w-72 bg-navy text-white flex flex-col animate-scale-in">
            <div className="px-5 py-5 border-b border-white/10 flex justify-between items-center">
              <Shield size={38} withText />
              <button data-testid="close-menu-btn" onClick={() => setOpen(false)}><X className="w-5 h-5" /></button>
            </div>
            <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
              {NAV.filter((n) => !n.ownerOnly || user?.role === "owner").map((n) => (
                <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)}
                  className={({ isActive }) => `flex items-center gap-3 px-3 py-3 rounded-xl text-sm font-medium ${isActive ? "bg-brandred" : "text-slate-300 hover:bg-white/10"}`}>
                  <n.icon style={{ width: 18, height: 18 }} /> {n.label}
                </NavLink>
              ))}
            </nav>
          </aside>
        </div>
      )}

      {/* Main */}
      <div className="flex-1 lg:ml-64 flex flex-col min-h-screen pb-20 lg:pb-0">
        <header className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-slate-200 px-4 lg:px-8 py-3 flex items-center gap-3">
          <button data-testid="open-menu-btn" className="lg:hidden text-navy" onClick={() => setOpen(true)}><Menu className="w-6 h-6" /></button>
          <div className="lg:hidden"><Shield size={32} /></div>
          <div className="flex-1 hidden sm:block"><SearchBar /></div>
          <div className="flex-1 sm:hidden" />
          <NavLink to="/ai" data-testid="topbar-ask-ai" className="hidden sm:flex items-center gap-2 bg-navy text-white text-sm font-semibold px-4 py-2 rounded-xl hover:bg-navy-deep transition-colors">
            <Bot className="w-4 h-4" /> Ask AI
          </NavLink>
        </header>
        <main className="flex-1 p-4 lg:p-8 animate-page-in">
          <Outlet />
        </main>
        <footer className="hidden lg:block px-8 py-4 text-xs text-slate-400 border-t border-slate-200">
          An AI-powered roofing business management platform — an operating system for running the entire company from lead to cash.
        </footer>
      </div>

      {/* Mobile bottom nav */}
      <nav className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-navy text-white flex justify-around items-center h-[60px] border-t border-white/10">
        {MOBILE_NAV.map((n) => (
          <NavLink key={n.to} to={n.to} end={n.end} data-testid={`mnav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            className={({ isActive }) => `flex flex-col items-center justify-center gap-0.5 text-[10px] font-semibold flex-1 h-full ${isActive ? "text-brandred" : "text-slate-300"}`}>
            <n.icon style={{ width: 20, height: 20 }} /> {n.label}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}
